import type { PromptCard } from '@promptvault/shared';
import { apiFetch } from './client';

/**
 * A home rail as the server composes it.
 *
 * `poolSize` is the rail's full cached length, of which `items` is this session's window. The
 * client uses it for two things: to know whether scrolling right will keep paying out, and to
 * print a real count in the section header instead of the number of cards that happen to be
 * rendered.
 */
export interface HomeRail {
  id: string;
  title: string;
  source: string;
  items: PromptCard[];
  poolSize: number;
  note?: string;
}

/** Mirrors `CurationConfig['announcement']`; the time window is applied server-side. */
export interface HomeAnnouncement {
  text: string;
  textFa?: string;
  url?: string;
}

export interface HomeScreen {
  rails: HomeRail[];
  promptOfTheDay: PromptCard | null;
  /**
   * Category slugs in the curator's order — slugs, not resolved names.
   *
   * The client already holds the taxonomy, with counts. Resolving server-side would cost a
   * `categories` lookup on the hottest path in the product and would hand back a `{slug,name}`
   * pair with no count, so the chips would lose the number they currently show.
   */
  categoryChips: string[];
  announcement: HomeAnnouncement | null;
}

/**
 * The whole home screen in one request.
 *
 * Home used to fire three separate `/feed` calls with `limit: 10` — three round trips, three
 * rankings, and a hard stop at ten cards that made "See all" the only way to see anything. One
 * call now returns every rail, each already rotated to this session's window.
 *
 * `refresh` advances every rail by a full window, which is what pull-to-refresh is wired to: the
 * gesture has to show genuinely different prompts or it is a lie about being interactive.
 */
export function fetchHome(opts: { language?: string; perRail?: number; refresh?: boolean } = {}) {
  return apiFetch<HomeScreen>('/home', {
    params: {
      ...(opts.language ? { language: opts.language } : {}),
      ...(opts.perRail ? { perRail: String(opts.perRail) } : {}),
      ...(opts.refresh ? { refresh: '1' } : {}),
    },
  });
}
